'use client';

import dynamic from 'next/dynamic';
import Script from 'next/script';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { css } from '@/styled-system/css';

import type { SearchListingsRequest, SearchListingsResult } from '@/src/lib/api';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import { useRouter } from '@/src/i18n/navigation';
import { searchKeys, useSearchListings } from '@/src/lib/hooks/query';
import { markSearchHdpHistoryIntent } from '@/src/lib/navigation/searchHdpHistory';
import { buildSrpFilterCarryoverHref } from '@/src/lib/navigation/srpQueryCarryover';
import { mapStateKeys, useSetMapMarkers, useSetMapPinnedMarker, useSetMapPosition } from '@/src/lib/hooks';
import type { MapState } from '@/src/lib/hooks/map/mapStateKeys';
import type { FilterState } from '@/src/lib/filters';
import { useQueryClient } from '@zg-rentals/react-query5';
import { buildListingMarkers } from '@/src/lib/utils/listingTransforms';
import { normalizeV3Listings } from '@/src/lib/utils/v3Listings';
import { generateMarkerId } from '@/src/lib/utils/generateMarkerId';
import { getListingHdpHref } from '@/src/lib/utils/listingHref';
import { ListingsGridItem } from './ListingsGridItem';
import { ListingsSkeleton } from './ListingsSkeleton';
import { NoListingsCard, NoResultsPanel } from './NoResultsPanel';
import { useSrpLiveSearchRequest } from './useSrpLiveSearchRequest';
import { useSrpSlugCanonicalization } from './srpSlugCanonicalization';
import { useMobileFeedTransitionScroll } from './useMobileFeedTransitionScroll';
import { useSrpSaveSearch } from './SrpSaveSearchProvider';
import { useSrpViewMode } from './ViewModeContext';
import { MIN_LG } from '@/src/lib/breakpoints';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { SrpAdSlot } from './SrpAdSlot';
import { useSrpAdTargeting } from '@/src/lib/ads/useSrpAdTargeting';
import { useSrpGoogleAds } from '@/src/lib/ads/useSrpGoogleAds';
import { getSrpAdSlotPlacements, GPT_SCRIPT_SRC, type SrpAdSlotPlacement } from '@/src/lib/ads/constants';
import { getAdListingType } from '@/src/lib/ads/adListingType';
import { useSrpLiveSearchPageView } from '@/src/analytics/useSrpLiveSearchPageView';

const FeedView = dynamic(() => import('@/src/components/features/search/FeedView').then((m) => m.FeedView), {
  ssr: false,
});

// Flip to a 2-column listings grid when the persistent-shell content panel
// is wide enough to fit two cards comfortably. Keyed to the panel's own
// width (not the viewport) — the `srpContentPanel` container is scoped to
// the lg+ split layout in `persistentSearchLayout.styles.ts`, so this rule
// has no effect below lg.
const SRP_TWO_COL_MIN = '36em';
const EMPTY_AD_PLACEMENTS: ReadonlyArray<SrpAdSlotPlacement> = [];
const MOBILE_GRID_PRIORITY_COUNT = 1;
const DESKTOP_GRID_PRIORITY_COUNT = 4;
const INITIAL_INTERACTIVE_LISTING_COUNT = 4;

function useGridImagePriorityCount(): number {
  // Mobile-first SSR default: the desktop count emits 4 high-priority preloads
  // that compete with the LCP image on mobile; desktop upgrades on hydration.
  const [priorityCount, setPriorityCount] = useState(MOBILE_GRID_PRIORITY_COUNT);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(MIN_LG);
    const updatePriorityCount = () => {
      setPriorityCount(mediaQuery.matches ? DESKTOP_GRID_PRIORITY_COUNT : MOBILE_GRID_PRIORITY_COUNT);
    };

    updatePriorityCount();
    mediaQuery.addEventListener('change', updatePriorityCount);
    return () => mediaQuery.removeEventListener('change', updatePriorityCount);
  }, []);

  return priorityCount;
}

type Props = {
  initialListings: Array<ExtendedListing>;
  initialListingsData: SearchListingsResult | null;
  initialParams: SearchListingsRequest;
  initialMapState?: MapState | null;
  areaId: string;
  page: number;
  propertyType: string;
  resourceId: string;
  /** Server-resolved unfiltered count for the zero-results panel; null when unknown. */
  initialUnfilteredTotal?: number | null;
  /** Server-computed slug-merged filter set, for the panel's first paint. */
  initialEffectiveFilters?: FilterState;
  isAgentic?: boolean;
};

/**
 * Live-updating listings grid (V3 search).
 *
 * First render uses the server-resolved listings (SSR + SEO) via initialData.
 * As the user pans/zooms or changes filters, `useSrpLiveSearchRequest` rebuilds
 * the V3 request, which changes the query key and triggers a refetch. Markers
 * are kept in sync as new data arrives.
 *
 * Server still owns the initial listings fetch; this component hydrates from
 * the SSR-prefetched cache entry for the initial request (no duplicate fetch
 * on mount because initialData is provided).
 */
export function LiveListingsGrid({
  initialListings,
  initialListingsData,
  initialParams,
  initialMapState,
  areaId,
  page,
  propertyType,
  resourceId,
  initialUnfilteredTotal,
  initialEffectiveFilters,
  isAgentic = false,
}: Props) {
  const t = useTranslations('search');
  const router = useRouter();
  const searchParams = useSearchParams();
  const feedViewRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const setMapPosition = useSetMapPosition();
  const setMapMarkers = useSetMapMarkers();
  const setMapPinnedMarker = useSetMapPinnedMarker();
  const { viewMode, setViewMode } = useSrpViewMode();
  const isDesktop = useIsDesktop();
  const gridPriorityCount = useGridImagePriorityCount();
  const {
    isSaved: isSavedSearch,
    isDisabled: isSaveSearchDisabled,
    handleToggle: handleToggleSaveSearch,
  } = useSrpSaveSearch();

  const isFeedActive = viewMode === 'feed';
  const isMobileFeedActive = isFeedActive && !isDesktop;
  const isFeedScrollSettled = useMobileFeedTransitionScroll(isMobileFeedActive);
  const isFeedLayoutReady = isDesktop || isFeedScrollSettled;

  useLayoutEffect(() => {
    if (!isFeedActive || !isFeedLayoutReady) return;

    const feedView = feedViewRef.current;
    if (!feedView) return;

    if (isDesktop) {
      feedView.style.removeProperty('--srp-feed-top-offset');
      feedView.style.removeProperty('height');
      return;
    }

    let animationFrame = 0;

    const updateFeedTopOffset = () => {
      animationFrame = 0;
      const actionsNav = document.getElementById('srp-actions-nav');
      const feedTop = Math.max(0, Math.round(actionsNav?.getBoundingClientRect().bottom ?? 0));
      const viewportBottom = window.visualViewport?.height ?? window.innerHeight;
      feedView.style.setProperty('--srp-feed-top-offset', `${feedTop}px`);
      feedView.style.setProperty('--srp-feed-height', `${Math.max(0, Math.round(viewportBottom - feedTop))}px`);
    };

    const requestFeedTopOffsetUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updateFeedTopOffset);
    };

    updateFeedTopOffset();
    window.addEventListener('resize', requestFeedTopOffsetUpdate);
    window.visualViewport?.addEventListener('resize', requestFeedTopOffsetUpdate);
    window.visualViewport?.addEventListener('scroll', requestFeedTopOffsetUpdate);

    const headerResizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            requestFeedTopOffsetUpdate();
          });
    const actionsNav = document.getElementById('srp-actions-nav');
    if (actionsNav) headerResizeObserver?.observe(actionsNav);

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', requestFeedTopOffsetUpdate);
      window.visualViewport?.removeEventListener('resize', requestFeedTopOffsetUpdate);
      window.visualViewport?.removeEventListener('scroll', requestFeedTopOffsetUpdate);
      headerResizeObserver?.disconnect();
    };
  }, [isDesktop, isFeedActive, isFeedLayoutReady]);

  // Seed React Query with the server-resolved initial result under the key that
  // matches the initial request. Uses setQueryData in an effect so it runs once
  // on mount without racing the live query below.
  useEffect(() => {
    if (!initialListingsData) return;
    queryClient.setQueryData(searchKeys.listings(initialParams), initialListingsData);
    // Intentionally only on mount: initialParams/initialListingsData don't change
    // during the lifetime of this page render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Backstop the null MapStateHydrator used by the streamed page shell. During
  // client-side transitions from the homepage, the listings grid can hydrate
  // before the desktop shell observes a map position, leaving the map waiting
  // even though listings are already visible. This seeds the same server-derived
  // state from the visible client component, without overwriting an existing
  // SRP/HDP map position unless it was seeded for a different area.
  useEffect(() => {
    if (!initialMapState) return;
    const existing = queryClient.getQueryData<MapState>(mapStateKeys.position());
    const shouldReplaceExisting = !existing || existing.areaKey == null || existing.areaKey !== areaId;
    if (!shouldReplaceExisting) return;
    setMapPosition(
      initialMapState.bounds,
      initialMapState.center,
      initialMapState.zoom,
      initialMapState.source,
      areaId,
    );
  }, [areaId, initialMapState, queryClient, setMapPosition]);

  const liveParams = useSrpLiveSearchRequest({ initialParams, areaId, page });
  const isInitialParams = liveParams === initialParams;

  const {
    data: liveData,
    isPlaceholderData,
    isError: isSearchError,
    fetchStatus: searchFetchStatus,
  } = useSearchListings(liveParams, {
    // Don't refetch on mount — the server fetch just happened and is in cache
    // via the seed effect above (or via initial params match).
    refetchOnMount: false,
    initialData: isInitialParams ? (initialListingsData ?? undefined) : undefined,
  });

  const liveSearchPageViewRequestKey = useSrpLiveSearchPageView({
    initialRequest: initialParams,
    request: liveParams,
    data: liveData,
    isPlaceholderData,
    isError: isSearchError,
    fetchStatus: searchFetchStatus,
    isAgentic,
  });

  // Morph the URL to the BE-resolved slug as filters change (promote/demote), via
  // a single router.replace (keep params, no strip → no blank). h1/meta follow
  // from the new slug's server render.
  useSrpSlugCanonicalization(liveData?.resolved, liveSearchPageViewRequestKey);

  const effectiveData = liveData ?? initialListingsData ?? undefined;
  const shouldUseInitialListings =
    isInitialParams ||
    liveData === undefined ||
    liveData === initialListingsData ||
    (isPlaceholderData && liveData === initialListingsData);

  const listings: Array<ExtendedListing> = useMemo(
    () => (shouldUseInitialListings ? initialListings : normalizeV3Listings(effectiveData?.listings)),
    [shouldUseInitialListings, initialListings, effectiveData],
  );

  // Sync markers to cache as new listings come in (initial load + any pan/zoom).
  // `listings` is already the normalized array for the current data, so reuse it
  // rather than re-normalizing effectiveData here.
  useEffect(() => {
    // A non-initial request with no live data yet is mid-flight (e.g. the
    // return-from-HDP refetch for a viewport panned while the HDP was open):
    // syncing now would stomp the current pins with stale initial markers.
    if (!isInitialParams && liveData === undefined) return;
    if (!effectiveData) return;
    const markers = buildListingMarkers(listings);
    setMapMarkers(markers);

    // If we just loaded an HDP route directly, pin the marker that matches.
    const hdpPrefix = `/${resourceId}/`;
    const match = markers.find((m) => m.listingData?.hdpUrl?.startsWith(hdpPrefix));
    if (match) {
      setMapPinnedMarker(match.markerId);
    }
  }, [effectiveData, isInitialParams, listings, liveData, resourceId, setMapMarkers, setMapPinnedMarker]);

  // Ads only exist on page 1's grid, so anything that keeps the grid from
  // rendering also keeps GAM from being asked for an ad.
  const adPlacements = useMemo(
    () => (page === 1 && !isMobileFeedActive ? getSrpAdSlotPlacements(listings.length) : EMPTY_AD_PLACEMENTS),
    [isMobileFeedActive, listings.length, page],
  );
  const adSlots = useMemo(() => adPlacements.map((placement) => placement.slot), [adPlacements]);
  // The BE-resolved filters carry the slug's own rental type (/rooms-for-rent),
  // which is what legacy reads off its effective redux filter.
  const adListingType = getAdListingType(
    effectiveData?.resolved?.effectiveFilters?.mustHave ?? liveParams.filters?.mustHave,
  );
  const adTargeting = useSrpAdTargeting({
    requestFilters: liveParams.filters,
    resolvedFilters: effectiveData?.resolved?.effectiveFilters,
    listings,
    enabled: adSlots.length > 0,
    adListingType,
  });
  useSrpGoogleAds(adSlots, adTargeting, adListingType);

  const gridItems = useMemo(() => {
    const items: Array<ReactNode> = listings.map((listing, index) => (
      <ListingsGridItem
        key={generateMarkerId(listing)}
        listing={listing}
        priority={index < gridPriorityCount}
        // SSR-safe desktop hint: `priority` starts mobile-first (one card) and
        // only upgrades to four after hydration — too late for desktop LCP
        // discovery (HP-8668). The lg-scoped preload covers the desktop
        // above-fold cards in the initial HTML without re-inflating mobile.
        desktopPriority={index < DESKTOP_GRID_PRIORITY_COUNT}
        initiallyInteractive={index < INITIAL_INTERACTIVE_LISTING_COUNT}
      />
    ));

    // Forward order, matching legacy's successive splices: each placement index
    // already accounts for the slots inserted ahead of it.
    for (const { slot, index } of adPlacements) {
      items.splice(index, 0, <SrpAdSlot key={slot.id} id={slot.id} />);
    }

    return items;
  }, [adPlacements, gridPriorityCount, listings]);

  const handleFeedListingClick = useCallback(
    (listing: ExtendedListing) => {
      const baseHref = getListingHdpHref(listing) || `/listings/${listing.aliasEncoded}`;
      const href = buildSrpFilterCarryoverHref(baseHref, searchParams);
      markSearchHdpHistoryIntent(href);
      setMapPinnedMarker(generateMarkerId(listing));
      router.push(href as Parameters<typeof router.push>[0]);
    },
    [router, searchParams, setMapPinnedMarker],
  );

  const totalListingsAvailable =
    effectiveData?.listings?.numUnits ?? effectiveData?.listings?.numBuildingsAvailable ?? listings.length;

  // `liveData`, NOT `effectiveData`: the latter falls back to the SSR result, which
  // would make an errored live search look settled and let the panel below claim a
  // count it does not have. Placeholder data belongs to the previous request.
  const hasSettledSearchData = liveData !== undefined && !isPlaceholderData;
  // Nothing left in flight for the current request: it errored, or
  // `useSearchListings` disabled it for want of a finite bounding box. Read only
  // when there is no settled data, so a healthy resolved query never lands here.
  const isSearchInFlight = !isSearchError && searchFetchStatus !== 'idle';

  if (listings.length === 0) {
    if (!hasSettledSearchData) {
      // No count to report, so say the neutral thing rather than blaming filters.
      return isSearchInFlight ? <ListingsSkeleton ariaLabel={t('loadingRentalListingsAria')} /> : <NoListingsCard />;
    }

    // A page past the last one is empty without the search itself being empty,
    // so only the genuine zero-result case gets the filter-relief panel. The
    // server-resolved count describes the SSR request only.
    return totalListingsAvailable === 0 ? (
      <NoResultsPanel
        request={liveParams}
        initialUnfilteredTotal={isInitialParams ? initialUnfilteredTotal : null}
        initialEffectiveFilters={isInitialParams ? initialEffectiveFilters : undefined}
      />
    ) : (
      <NoListingsCard />
    );
  }

  if (isMobileFeedActive) {
    return (
      <div
        ref={feedViewRef}
        id="srp-feed-view"
        data-srp-component="feed-view"
        data-srp-feed-transition-ready={isFeedLayoutReady ? 'true' : 'false'}
        className={css({
          position: { base: 'fixed', lg: 'relative' },
          top: { base: 'var(--srp-feed-top-offset, 0px)', lg: 'auto' },
          right: { base: 0, lg: 'auto' },
          left: { base: 0, lg: 'auto' },
          zIndex: { base: 'zIndexParent', lg: 'auto' },
          height: {
            base: 'var(--srp-feed-height, 100vh)',
            lg: '100%',
          },
          width: '100%',
          marginInline: 0,
          minHeight: { base: 0, lg: 0 },
          display: 'flex',
          flexDirection: 'column',
          '&[data-srp-feed-transition-ready="false"]': {
            visibility: 'hidden',
          },
          lg: {
            flex: '1 1 0%',
          },
        })}
      >
        <FeedView
          listings={listings}
          totalListingsAvailable={totalListingsAvailable}
          onListingClick={handleFeedListingClick}
          onBackToList={() => setViewMode('grid')}
          isSaveSearchSaved={isSavedSearch}
          isSaveSearchDisabled={isSaveSearchDisabled}
          onToggleSaveSearch={handleToggleSaveSearch}
          hideMobileHeader
          hideShellHeaderOnMobile={false}
        />
      </div>
    );
  }

  return (
    <>
      {/* lazyOnload keeps gpt.js off the LCP-critical window; queued googletag.cmd
          calls flush on load, and ad requests are consent-gated later anyway. */}
      {adSlots.length > 0 && <Script id="google-publisher-tags" strategy="lazyOnload" src={GPT_SCRIPT_SRC} />}
      <ul
        id="srp-listings-grid"
        data-srp-component="listings-grid"
        data-view-mode={viewMode}
        role="list"
        aria-label={t('listingsForRentAria', {
          count: listings.length,
          propertyType: propertyType.toLowerCase(),
        })}
        className={css({
          display: 'grid',
          // Two-column layout when the content panel itself is wide enough.
          // Explicit 2 columns (not `repeat(auto-fill, minmax(…))`) because
          // the panel is capped at 800px regardless of viewport, so we
          // always want exactly 2 columns above the threshold, never more.
          gridTemplateColumns: '1fr',
          [`@container srpContentPanel (min-width: ${SRP_TWO_COL_MIN})`]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
          gap: '400',
          marginBottom: '400',
          listStyle: 'none',

          padding: 0,

          margin: 0,
        })}
      >
        {gridItems}
      </ul>
    </>
  );
}
